import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { useQuery } from '@tanstack/react-query';
import { queries, type ComuneFull } from '@/data';
import { useChoroplethStore } from './store';
import { LAYERS_BY_ID } from './layers-config';
import { colorFor } from './colors';
import { comuneTooltipHtml } from './tooltips';
import { createIDWLayer, type IDWLayerInstance } from './IDWLayer';
import { nearestPoint, type IdwPoint } from './idw';

/**
 * Тепловая карта в стиле прогноза погоды: плавный градиент IDW-интерполяции
 * по значениям comuni.
 *
 * Соответствие с choropleth:
 * - Та же палитра (`colorFor`) — те же диапазоны цветов на тех же значениях.
 * - Tooltip при наведении показывает значение БЛИЖАЙШЕЙ comune (не IDW),
 *   поэтому переключение на choropleth не «теряет» цифры.
 */
export function HeatLayer() {
  const map = useMap();
  const { activeLayer, mode, overlayHidden } = useChoroplethStore();
  // US-07 follow-up: «Только карта» в radio'е выставляет overlayHidden=true.
  // Heatmap должна скрываться так же, как choropleth — иначе переключение
  // Heatmap → Map only визуально не срабатывает.
  const isActive = mode === 'heatmap' && !overlayHidden;
  const layerDef = LAYERS_BY_ID[activeLayer];
  const layerRef = useRef<IDWLayerInstance | null>(null);
  const tooltipRef = useRef<L.Tooltip | null>(null);
  const comuniRef = useRef<ComuneFull[]>([]);

  const { data: meta } = useQuery(queries.meta());
  // Lazy: full comuni.json только в heatmap-режиме.
  const { data: comuni } = useQuery({
    ...queries.full(meta?.version ?? ''),
    enabled: !!meta?.version && isActive,
  });

  // Создаём/обновляем IDW-слой при смене слоя данных или comuni.
  useEffect(() => {
    if (!isActive || !layerDef || !comuni) return;

    const idwPoints: IdwPoint[] = comuni.map((c) => ({
      lat: c.lat,
      lon: c.lon,
      value: layerDef.getValue(c),
    }));
    comuniRef.current = comuni;

    const layer = createIDWLayer({ pane: 'overlayPane' });
    layer.setData(idwPoints, (v) => colorFor(layerDef, v));
    layer.addTo(map);
    layerRef.current = layer;

    return () => {
      map.removeLayer(layer);
      layerRef.current = null;
      comuniRef.current = [];
    };
  }, [map, isActive, comuni, layerDef]);

  // Mousemove tooltip — показывает значение ближайшей comune. Это намеренно
  // не IDW-значение: юзер должен видеть те же числа, что были бы в choropleth.
  useEffect(() => {
    if (!isActive || !layerDef) return;
    const onMove = (e: L.LeafletMouseEvent) => {
      const layer = layerRef.current;
      const comuni = comuniRef.current;
      if (!layer || comuni.length === 0) return;
      const index = layer.getIndex();
      if (!index) return;
      const idx = nearestPoint(e.latlng.lat, e.latlng.lng, index);
      if (idx === null) {
        tooltipRef.current?.close();
        return;
      }
      const c = comuni[idx];
      const html = comuneTooltipHtml(c, layerDef);
      if (tooltipRef.current) {
        tooltipRef.current.setContent(html);
        tooltipRef.current.setLatLng(e.latlng);
      } else {
        tooltipRef.current = L.tooltip({
          direction: 'top',
          offset: [0, -8],
          opacity: 0.95,
          className: 'choropleth-tooltip',
          permanent: false,
        })
          .setContent(html)
          .setLatLng(e.latlng)
          .addTo(map);
      }
    };
    const onLeave = () => {
      tooltipRef.current?.close();
      tooltipRef.current?.remove();
      tooltipRef.current = null;
    };

    map.on('mousemove', onMove);
    map.on('mouseout', onLeave);
    return () => {
      map.off('mousemove', onMove);
      map.off('mouseout', onLeave);
      onLeave();
    };
  }, [map, isActive, layerDef]);

  return null;
}
